/* pauseTeadsHandler.tsx */
import Container from '@components/atoms/container/adsContainer';
import PauseAd from '@components/sections/pauseAd/pauseAd';
import TeadsErrorBoundary from './teadsErrorBoundary';
import TeadsUtils, { Placement } from '@shared/utils/teads';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef } from 'react';

/**
 * Teads constants
 */
const CONTAINER_ID = 'pause-teads-container';

/**
 * PauseTeadsUI component.
 * Handles the UI for the PauseTeadsHandler component.
 */
function PauseTeadsUI() {
  const { pauseAd } = useAppSelector(adsSelector);
  return (
    <Container>
      <div
        style={{ width: '100%', height: '100%' }}
        id={'pause-teads-container'}
      >
        <div
          style={{
            background: pauseAd?.active ? 'black' : 'none',
            position: 'absolute',
            height: '100%',
            width: '100%',
            zIndex: 1,
          }}
        />
      </div>
      {pauseAd?.active ? <PauseAd /> : null}
    </Container>
  );
}

/**
 * PauseTeadsHandlerContent component.
 * Handles the Teads pause ad placement.
 */
function PauseTeadsHandlerContent() {
  const { pauseAd } = useAppSelector(adsSelector);
  const inpausePlacement = useRef<any>(null);
  const teadsInstance = useRef<any>(null);

  /**
   * Initialize Teads instance and placement
   */
  useEffect(() => {
    if (!window.teads?.SDK) return;
    const { sdkInstance, placementInstance } = TeadsUtils.initializeTeads({
      asset: pauseAd?.asset,
      placementType: Placement.IN_PAUSE,
      placementId: parseInt(pauseAd?.placementId ?? ''),
      containerId: CONTAINER_ID,
    });
    inpausePlacement.current = placementInstance;
    teadsInstance.current = sdkInstance;
    return () => {
      sdkInstance?.destroy?.();
    };
  }, []);

  /**
   * Update content metadata and play ad
   */
  useEffect(() => {
    if (!teadsInstance.current) return;
    if (!inpausePlacement.current) return;
    // Trigger ad
    if (pauseAd) {
      // Update content metadata
      const metadata = TeadsUtils.buildContentMetadata(pauseAd?.asset);
      teadsInstance.current?.setContentMetaData(metadata);
      // Load and play ad
      if (!inpausePlacement.current?.isAdReady) {
        inpausePlacement.current.loadAd();
      } else {
        inpausePlacement.current.playAd();
      }
    }
    // Stop ad
    else {
      inpausePlacement.current.stopAd();
    }
  }, [pauseAd]);

  /**
   * Return the component
   */
  return <PauseTeadsUI />;
}

/**
 * PauseTeadsHandler main component.
 * Wraps the PauseTeadsHandlerContent component in a TeadsErrorBoundary component.
 */
export default function PauseTeadsHandler() {
  return (
    <TeadsErrorBoundary fallback={<PauseTeadsUI />}>
      <PauseTeadsHandlerContent />
    </TeadsErrorBoundary>
  );
}
